import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Avatar,
  Button,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  PostAdd,
  Person,
  Description,
  Schedule,
  TrendingUp,
  FilterList,
  Clear,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { postsAPI } from '../services/api';
import { useClients } from '../contexts/ClientContext';
import { format } from 'date-fns';

const SearchDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { clients } = useClients();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [searchResults, setSearchResults] = useState({
    posts: [],
    clients: [],
    content: [],
  });
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchInputRef = useRef(null);

  const searchTypes = [
    { label: 'All', value: 'all', icon: <SearchIcon /> },
    { label: 'Posts', value: 'posts', icon: <PostAdd /> },
    { label: 'Clients', value: 'clients', icon: <Person /> },
    { label: 'Content', value: 'content', icon: <Description /> },
  ];

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  const performSearch = async (query, type = 'all') => {
    if (!query.trim()) {
      setSearchResults({ posts: [], clients: [], content: [] });
      return;
    }

    setLoading(true);
    try {
      const results = { posts: [], clients: [], content: [] };

      if (type === 'all' || type === 'posts') {
        try {
          const posts = await postsAPI.getPosts();
          const filteredPosts = posts.filter(
            (post) =>
              post.content?.toLowerCase().includes(query.toLowerCase()) ||
              post.title?.toLowerCase().includes(query.toLowerCase()) ||
              post.hashtags?.toLowerCase().includes(query.toLowerCase()) ||
              post.platform?.toLowerCase().includes(query.toLowerCase())
          );
          results.posts = filteredPosts.slice(0, 10); // Limit results
        } catch (error) {
          console.error('Error searching posts:', error);
        }
      }

      if (type === 'all' || type === 'clients') {
        const filteredClients = clients.filter(
          (client) =>
            client.name?.toLowerCase().includes(query.toLowerCase()) ||
            client.email?.toLowerCase().includes(query.toLowerCase()) ||
            client.industry?.toLowerCase().includes(query.toLowerCase())
        );
        results.clients = filteredClients.slice(0, 10);
      }

      if (type === 'all' || type === 'content') {
        // For now, we'll search through posts content as "content"
        // This can be expanded to include AI-generated content, templates, etc.
        try {
          const posts = await postsAPI.getPosts();
          const contentPosts = posts.filter(
            (post) =>
              post.content?.toLowerCase().includes(query.toLowerCase()) ||
              post.ai_generated_content
                ?.toLowerCase()
                .includes(query.toLowerCase())
          );
          results.content = contentPosts.slice(0, 10);
        } catch (error) {
          console.error('Error searching content:', error);
        }
      }

      setSearchResults(results);

      // Save to recent searches
      if (query.trim()) {
        const newRecent = [
          query,
          ...recentSearches.filter((s) => s !== query),
        ].slice(0, 5);
        setRecentSearches(newRecent);
        localStorage.setItem('recentSearches', JSON.stringify(newRecent));
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const searchType = searchTypes[activeTab]?.value || 'all';
    performSearch(query, searchType);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (searchQuery) {
      const searchType = searchTypes[newValue]?.value || 'all';
      performSearch(searchQuery, searchType);
    }
  };

  const handleRecentSearch = (query) => {
    setSearchQuery(query);
    handleSearch(query);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults({ posts: [], clients: [], content: [] });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'draft':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'instagram':
        return '📷';
      case 'facebook':
        return '📘';
      case 'linkedin':
        return '💼';
      case 'twitter':
      case 'x':
        return '🐦';
      case 'youtube':
        return '📺';
      default:
        return '📱';
    }
  };

  const highlightText = (text, query) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: '#58a6ff',
            color: '#fff',
            padding: '1px 2px',
            borderRadius: '2px',
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const renderPostResult = (post) => (
    <ListItem
      key={post.id}
      sx={{
        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.1)' },
        cursor: 'pointer',
        borderRadius: 1,
        mb: 1,
      }}
      onClick={() => {
        navigate(`/posts?post_id=${encodeURIComponent(post.id)}`);
        onClose();
      }}
    >
      <ListItemIcon>
        <Avatar sx={{ bgcolor: 'rgba(88, 166, 255, 0.2)', color: '#58a6ff' }}>
          {getPlatformIcon(post.platform)}
        </Avatar>
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {highlightText(post.title || 'Untitled Post', searchQuery)}
            </Typography>
            <Chip
              label={post.status}
              size="small"
              color={getStatusColor(post.status)}
              variant="outlined"
            />
            <Chip
              label={post.platform}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" sx={{ color: '#8b949e', mb: 0.5 }}>
              {highlightText(
                post.content?.substring(0, 100) +
                  (post.content?.length > 100 ? '...' : ''),
                searchQuery
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {post.scheduled_time && (
                <Typography variant="caption" sx={{ color: '#6e7681' }}>
                  📅 {format(new Date(post.scheduled_time), 'MMM dd, yyyy')}
                </Typography>
              )}
              {post.engagement_metrics && (
                <Typography variant="caption" sx={{ color: '#6e7681' }}>
                  ❤️ {post.engagement_metrics.likes || 0}
                </Typography>
              )}
            </Box>
          </Box>
        }
      />
    </ListItem>
  );

  const renderClientResult = (client) => (
    <ListItem
      key={client.id}
      sx={{
        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.1)' },
        cursor: 'pointer',
        borderRadius: 1,
        mb: 1,
      }}
      onClick={() => {
        navigate(`/clients?client_id=${encodeURIComponent(client.id)}`);
        onClose();
      }}
    >
      <ListItemIcon>
        <Avatar sx={{ bgcolor: 'rgba(88, 166, 255, 0.2)', color: '#58a6ff' }}>
          <Person />
        </Avatar>
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {highlightText(client.name, searchQuery)}
          </Typography>
        }
        secondary={
          <Box>
            <Typography variant="body2" sx={{ color: '#8b949e', mb: 0.5 }}>
              {highlightText(client.email, searchQuery)}
            </Typography>
            {client.industry && (
              <Chip
                label={client.industry}
                size="small"
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
              />
            )}
          </Box>
        }
      />
    </ListItem>
  );

  const renderContentResult = (content) => (
    <ListItem
      key={content.id}
      sx={{
        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.1)' },
        cursor: 'pointer',
        borderRadius: 1,
        mb: 1,
      }}
      onClick={() => {
        navigate(`/posts?post_id=${encodeURIComponent(content.id)}`);
        onClose();
      }}
    >
      <ListItemIcon>
        <Avatar sx={{ bgcolor: 'rgba(88, 166, 255, 0.2)', color: '#58a6ff' }}>
          <Description />
        </Avatar>
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {highlightText(
              content.title || 'AI Generated Content',
              searchQuery
            )}
          </Typography>
        }
        secondary={
          <Typography variant="body2" sx={{ color: '#8b949e' }}>
            {highlightText(
              content.content?.substring(0, 100) +
                (content.content?.length > 100 ? '...' : ''),
              searchQuery
            )}
          </Typography>
        }
      />
    </ListItem>
  );

  const totalResults =
    searchResults.posts.length +
    searchResults.clients.length +
    searchResults.content.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#161b22',
          border: '1px solid #30363d',
          borderRadius: 2,
          minHeight: '600px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #30363d',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon sx={{ color: '#58a6ff' }} />
          <Typography variant="h6" sx={{ color: '#f0f6fc', fontWeight: 600 }}>
            Search
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#8b949e' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #30363d' }}>
          <TextField
            inputRef={searchInputRef}
            fullWidth
            placeholder="Search posts, clients, content..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#8b949e' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearch} size="small">
                    <Clear sx={{ color: '#8b949e' }} />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                color: '#f0f6fc',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#21262d',
                  '& fieldset': { borderColor: '#30363d' },
                  '&:hover fieldset': { borderColor: '#58a6ff' },
                  '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#8b949e',
                },
              },
            }}
          />
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: '1px solid #30363d',
            '& .MuiTab-root': {
              color: '#8b949e',
              textTransform: 'none',
              fontWeight: 500,
              '&.Mui-selected': {
                color: '#58a6ff',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#58a6ff',
            },
          }}
        >
          {searchTypes.map((type, index) => (
            <Tab
              key={type.value}
              label={type.label}
              icon={type.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>

        <Box sx={{ p: 3, minHeight: '400px' }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <CircularProgress sx={{ color: '#58a6ff' }} />
            </Box>
          ) : searchQuery ? (
            <Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#f0f6fc', fontWeight: 600 }}
                >
                  {totalResults} result{totalResults !== 1 ? 's' : ''} for "
                  {searchQuery}"
                </Typography>
                <FilterList sx={{ color: '#8b949e', fontSize: 20 }} />
              </Box>

              {totalResults === 0 ? (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: '#21262d',
                    border: '1px solid #30363d',
                  }}
                >
                  <SearchIcon sx={{ fontSize: 48, color: '#8b949e', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#8b949e', mb: 1 }}>
                    No results found
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6e7681' }}>
                    Try different keywords or check your spelling
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  {(activeTab === 0 || activeTab === 1) &&
                    searchResults.posts.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#f0f6fc', mb: 1, fontWeight: 600 }}
                        >
                          Posts ({searchResults.posts.length})
                        </Typography>
                        <List
                          sx={{
                            bgcolor: '#21262d',
                            borderRadius: 1,
                            border: '1px solid #30363d',
                          }}
                        >
                          {searchResults.posts.map(renderPostResult)}
                        </List>
                      </Box>
                    )}

                  {(activeTab === 0 || activeTab === 2) &&
                    searchResults.clients.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#f0f6fc', mb: 1, fontWeight: 600 }}
                        >
                          Clients ({searchResults.clients.length})
                        </Typography>
                        <List
                          sx={{
                            bgcolor: '#21262d',
                            borderRadius: 1,
                            border: '1px solid #30363d',
                          }}
                        >
                          {searchResults.clients.map(renderClientResult)}
                        </List>
                      </Box>
                    )}

                  {(activeTab === 0 || activeTab === 3) &&
                    searchResults.content.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#f0f6fc', mb: 1, fontWeight: 600 }}
                        >
                          Content ({searchResults.content.length})
                        </Typography>
                        <List
                          sx={{
                            bgcolor: '#21262d',
                            borderRadius: 1,
                            border: '1px solid #30363d',
                          }}
                        >
                          {searchResults.content.map(renderContentResult)}
                        </List>
                      </Box>
                    )}
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ color: '#f0f6fc', mb: 2, fontWeight: 600 }}
              >
                Recent Searches
              </Typography>
              {recentSearches.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {recentSearches.map((search, index) => (
                    <Chip
                      key={index}
                      label={search}
                      onClick={() => handleRecentSearch(search)}
                      sx={{
                        bgcolor: 'rgba(88, 166, 255, 0.1)',
                        color: '#58a6ff',
                        border: '1px solid rgba(88, 166, 255, 0.3)',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'rgba(88, 166, 255, 0.2)',
                        },
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: '#21262d',
                    border: '1px solid #30363d',
                  }}
                >
                  <SearchIcon sx={{ fontSize: 48, color: '#8b949e', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#8b949e', mb: 1 }}>
                    Start searching
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6e7681' }}>
                    Search across posts, clients, and content
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
